'use client';

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Element, useEditor, useNode } from '@craftjs/core';
import {
  ATRIBUTO_AREA_LIBRE,
  ID_CAPA_LIBRE,
  PROP_POSICION,
  ANCHO_COMPOSICION_MOVIL,
  altoRelativoMovil,
  esMarco,
  normalizarPosicion,
  type PoliticaResize,
} from './posicionLibre';

/**
 * Piezas de una sección prediseñada.
 *
 * ## El problema
 *
 * Las secciones —Hero, FAQ, CTA…— traen su título, su subtítulo y sus botones
 * escritos a mano en el JSX. Para Craft son UN solo nodo: no se puede
 * seleccionar el botón del Hero, ni moverlo, ni cambiarle el tamaño. Lo único
 * editable es el contenido, vía `EditableText`, que escribe un prop de la
 * sección.
 *
 * ## La idea
 *
 * Darle a cada pieza visual **identidad propia en Craft sin sacarle el contenido
 * a la sección**. Cada pieza es un `<Element>` enlazado (`linkedNodes`) del nodo
 * de la sección, con id `pieza:<clave>`, cuyo componente —`PiezaLibre`— no tiene
 * markup propio: renderiza el JSX que la sección le pasa por contexto de React.
 *
 * De ahí salen las tres propiedades que hacían falta:
 *
 * - **Identidad**: el nodo existe, se selecciona y se mueve con la maquinaria de
 *   `posicionLibre` / `RenderNodoLibre` / `ControlesLibres` que ya está andando
 *   para los elementos sueltos. No hay un segundo sistema de arrastre.
 * - **Una sola fuente de verdad**: el texto sigue siendo `props.title` de la
 *   sección. La pieza guarda SOLO su `_layout`. Cambiar el título desde el panel
 *   lateral sigue funcionando igual.
 * - **El diseño no se congela en el JSON**: las clases y los colores viajan por
 *   contexto, que no se serializa. Si mañana cambia el diseño del Hero, las
 *   páginas guardadas lo toman; si el markup viviera en props del nodo, no.
 *
 * ## Qué ve una página que nunca usó esto
 *
 * Exactamente lo de antes. Sin `_layout` la pieza devuelve su contenido tal cual
 * y `RenderNodoLibre` no la envuelve en nada: mismo DOM, mismo layout. El único
 * agregado es un `<div style="display:contents">`, que por definición no
 * participa del layout.
 *
 * MANTENER EN SYNC con goautos-admin/src/components/builder2/utils/piezaLibre.tsx.
 * La única diferencia permitida es el congelado (`congelarPiezas`), que solo
 * existe en el editor.
 */

/** Prefijo del id enlazado. `linkedNodes['pieza:titulo']` = nodo de esa pieza. */
export const PREFIJO_PIEZA = 'pieza:';

/** Nombre del componente en el resolver de los dos repos. */
export const NOMBRE_PIEZA = 'PiezaLibre';

/** Marca el envoltorio de una pieza; lleva el id de su nodo. Sirve para medirla. */
export const ATRIBUTO_PIEZA = 'data-pieza';

/**
 * Qué CLASE de pieza es, en el DOM.
 *
 * Lo lee la auditoría para probar por CAPACIDAD y no sección por sección: sin
 * esto habría que mantener una tabla paralela de qué es cada pieza, que es justo
 * lo que se desincroniza.
 */
export const ATRIBUTO_TIPO_PIEZA = 'data-pieza-tipo';

/** Marca el contenedor que hace de marco de referencia de las piezas. */
export const ATRIBUTO_AREA_PIEZAS = 'data-area-piezas';

/**
 * Alto que tenía el área cuando se congeló el diseño, en px.
 *
 * Al pasar a posición libre TODAS las piezas se vuelven `absolute` y el
 * contenedor se queda sin nada que lo empuje: sin esto la sección se desplomaría
 * a cero. Se guarda una vez, al congelar, con el alto real de ese momento, así
 * el paso a libre es visualmente invisible.
 *
 * Ausente = la sección nunca se congeló = comportamiento de siempre.
 */
export const PROP_ALTO_PIEZAS = '_altoPiezas';

/**
 * Dónde se guardan la posición y el alto MIENTRAS el modo libre está apagado.
 *
 * "Diseño original" es un interruptor de MODO, no un borrado: apagarlo tenía
 * que dejar de aplicar las coordenadas, no perderlas. Antes las borraba, así que
 * mirar cómo venía la sección de fábrica costaba todo lo acomodado.
 *
 * Van en props aparte y no en el mismo: el sitio decide si una pieza está
 * posicionada por la PRESENCIA de `_layout`, y guardarlas ahí con una bandera
 * obligaría a los dos repos a entender un estado más. Con esto, para el sitio
 * una sección apagada es indistinguible de una que nunca se tocó.
 */
export const PROP_POSICION_GUARDADA = '_layoutGuardado';
export const PROP_ALTO_GUARDADO = '_altoPiezasGuardado';

/**
 * Qué CLASE de cosa es una pieza, y qué le corresponde por eso.
 *
 * Antes cada pieza declaraba solo su política de resize, así que una convertida
 * sin pensarlo se quedaba con la de por defecto —`ancho`, dos manijas— y sin
 * ninguna barra: `RenderNodoLibre` elige la barra contextual por el NOMBRE del
 * componente, y el de una pieza siempre es `PiezaLibre`. El resultado era que
 * un título dentro de una sección se comportaba peor que un Texto suelto.
 *
 * Acá la pieza declara QUÉ ES y de eso sale todo: las manijas, qué se guarda al
 * redimensionar y qué barra se le dibuja. Convertir una sección nueva es elegir
 * un tipo, no volver a cablear resize + barra + edición.
 */
export type TipoPieza = 'texto' | 'imagen' | 'bloque' | 'coleccion';

export interface CapacidadPieza {
  politica: PoliticaResize;
  /** Barra contextual, si su contenido tiene una que sirva. */
  barra: 'texto' | null;
}

/**
 * Todas redimensionan en los dos ejes: las ocho manijas, como la Imagen.
 *
 * Es a propósito que no haya un tipo con manijas laterales solamente. Un título
 * al que solo se le puede tocar el ancho no se puede alinear en vertical dentro
 * de su caja —no hay sobrante que repartir— y el dealer no tiene forma de saber
 * por qué esa pieza tiene menos manijas que la de al lado.
 *
 * La diferencia entre tipos no está en las manijas sino en la BARRA: solo el
 * texto tiene una que hace algo. Una barra de imagen sobre una pieza escribiría
 * `src` en el nodo de la pieza, que su render ni mira; sería un control muerto.
 */
export const CAPACIDADES: Record<TipoPieza, CapacidadPieza> = {
  texto: { politica: 'ambos', barra: 'texto' },
  imagen: { politica: 'proporcional', barra: null },
  bloque: { politica: 'ambos', barra: null },
  coleccion: { politica: 'ambos', barra: null },
};

/**
 * Props de tipografía que la barra de texto escribe sobre la pieza.
 *
 * Son los mismos nombres que usa `BarraTexto` con un Texto suelto, así que la
 * barra no necesita saber que está sobre una pieza.
 */
const TIPOGRAFIA = [
  ['fontSize', 'font-size', (v: unknown) => `${Number(v)}px`],
  ['fontWeight', 'font-weight', (v: unknown) => String(v)],
  ['color', 'color', (v: unknown) => String(v)],
  ['textAlign', 'text-align', (v: unknown) => String(v)],
] as const;

/**
 * El TEXTO de la pieza: lo que el dealer escribe.
 *
 * Es la única definición de "acá hay texto" que existe, y la usan las dos
 * puntas: la que aplica la tipografía elegida y la que la mide para mostrar el
 * número en la barra. Cuando cada una tenía la suya, la barra medía un `<p>` de
 * 12 px, el estilo caía además en el flex que lo envuelve, y el número de la
 * barra no era el de ningún texto de la pantalla.
 *
 * Se prefieren los campos editables —`data-et`— y no cualquier cosa con letras,
 * porque una sección también dibuja letras que son DECORACIÓN: la comilla de
 * `10rem` de un testimonio es un carácter suelto puesto de adorno. Midiendo por
 * ahí la barra decía 160 px, que es el tope, y agrandar el testimonio no movía
 * nada; aplicando por ahí, agrandar la cita le comía el adorno.
 *
 * El `textContent` de un ancestro tampoco sirve: en un antetítulo con dos
 * rayitas el ancestro es un flex que no dibuja nada y su `font-size` es el
 * heredado. Por eso el respaldo —para el texto que todavía es un literal del
 * JSX— pide nodo de texto PROPIO.
 */
export const elementosConTexto = (raiz: HTMLElement): HTMLElement[] => {
  const editables = Array.from(raiz.querySelectorAll('[data-et]')) as HTMLElement[];
  if (editables.length) return editables;
  return (Array.from(raiz.querySelectorAll('*')) as HTMLElement[]).filter((el) =>
    Array.from(el.childNodes).some(
      (n) => n.nodeType === 3 && (n.textContent ?? '').trim().length > 0,
    ),
  );
};

/**
 * Aplica la tipografía elegida al contenido de la pieza.
 *
 * Se hace sobre el DOM y con `important` porque el contenido es el JSX de la
 * sección: trae sus clases de Tailwind y sus estilos en línea, y un valor
 * heredado desde el envoltorio pierde contra los dos. El envoltorio, además, es
 * `display: contents` y no tiene caja propia donde ponerlo.
 *
 * Solo se tocan las propiedades que alguien eligió. Mientras la pieza no tenga
 * ninguna, no se escribe ni se borra nada y el contenido queda exactamente como
 * lo pintó la sección.
 */
const useTipografiaDePieza = (
  envoltorio: HTMLElement | null,
  props: Record<string, unknown>,
) => {
  /**
   * Qué propiedades escribió ESTE hook.
   *
   * Es la parte que no se puede saltear: la sección pinta su contenido con sus
   * propios estilos en línea —`style={{ color: textColor }}` está en media
   * docena de secciones— y borrar a ciegas una propiedad que nadie eligió acá se
   * los llevaba puestos. Solo se quita lo que se puso.
   */
  const puestas = useRef<Set<string>>(new Set());
  const firma = TIPOGRAFIA.map(([prop]) => String(props?.[prop] ?? '')).join('|');

  useEffect(() => {
    if (!envoltorio) return;
    // Los hijos directos —ahí manda `text-align`, que se hereda y no lo pisa
    // nadie— MÁS todo lo que lleve texto propio. Quedarse en los hijos directos
    // fallaba cuando la pieza envuelve un contenedor: en un antetítulo con sus
    // dos rayitas, el `font-size` caía en el flex y el `<p>` de adentro seguía
    // ganando con su clase de Tailwind. La barra cambiaba el prop y no se veía.
    const destinos = new Set<HTMLElement>(Array.from(envoltorio.children) as HTMLElement[]);
    elementosConTexto(envoltorio).forEach((el) => destinos.add(el));

    for (const [prop, css, formato] of TIPOGRAFIA) {
      const valor = props?.[prop];
      const elegido = valor !== undefined && valor !== null && valor !== '';
      if (elegido) {
        destinos.forEach((h) => h.style.setProperty(css, formato(valor), 'important'));
        puestas.current.add(css);
      } else if (puestas.current.has(css)) {
        destinos.forEach((h) => h.style.removeProperty(css));
        puestas.current.delete(css);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envoltorio, firma]);
};

interface DatosPieza {
  etiqueta: string;
  tipo: TipoPieza;
  politica: PoliticaResize;
  barra: 'texto' | null;
  contenido: React.ReactNode;
}

const ContextoPieza = createContext<DatosPieza | null>(null);

/**
 * En qué está la pieza que se está renderizando.
 *
 * - `null` — no es una pieza, o la sección sigue en su diseño original. Todo se
 *   comporta como antes de que esto existiera: un clic en el texto lo edita.
 * - `'seleccion'` — la pieza es un objeto que se agarra. El texto NO es
 *   `contentEditable`, así que el clic atraviesa hasta el nodo y lo selecciona:
 *   es lo que hace que se pueda agarrar un título sin buscarle el píxel.
 * - `'edicion'` — doble clic: vuelve a ser texto editable, con la barra
 *   flotante y todo, sin perder la selección de la pieza.
 */
export type ModoPieza = 'seleccion' | 'edicion';

const ContextoModo = createContext<ModoPieza | null>(null);

/** Datos de la pieza que se está renderizando, o null si el nodo no es una pieza. */
export const usePiezaActual = (): DatosPieza | null => useContext(ContextoPieza);

/**
 * Modo de la pieza que envuelve a este componente, o `null` si no hay ninguna.
 *
 * Lo consume `EditableText`: es lo único que necesita saber para decidir si el
 * clic edita o selecciona.
 */
export const useModoPieza = (): ModoPieza | null => useContext(ContextoModo);

/**
 * El nodo de la pieza. No dibuja nada propio: saca el contenido del contexto.
 *
 * El `<div display:contents>` está para dos cosas y ninguna es visual: darle a
 * Craft un elemento que conectar —sin él la pieza no se podría seleccionar— y
 * dejar una marca en el DOM para poder medir la pieza al congelar. `contents`
 * hace que la caja no exista para el layout: los hijos quedan donde estaban,
 * incluso adentro de un flex o un grid.
 *
 * `connect` se aplica SOLO cuando la pieza ya tiene posición. Mientras no la
 * tiene, el click tiene que seguir cayendo en la sección —para editar el texto y
 * ver sus settings— tal como antes de que esto existiera.
 */
export const PiezaLibre: React.FC = () => {
  const datos = useContext(ContextoPieza);
  const [editando, setEditando] = useState(false);
  const {
    connectors: { connect },
    id,
    posicionada,
    seleccionada,
    props,
  } = useNode((node) => ({
    posicionada: !!normalizarPosicion(
      (node.data.props as Record<string, unknown>)?.[PROP_POSICION],
    ),
    seleccionada: node.events.selected,
    props: node.data.props as Record<string, unknown>,
  }));
  const [envoltorio, setEnvoltorio] = useState<HTMLElement | null>(null);
  useTipografiaDePieza(envoltorio, props);

  // Soltar la pieza cierra la edición: si no, un campo quedaría `contentEditable`
  // por detrás y el siguiente clic ahí escribiría en vez de seleccionar.
  useEffect(() => {
    if (!seleccionada || !posicionada) setEditando(false);
  }, [seleccionada, posicionada]);

  const modo: ModoPieza | null = !posicionada ? null : editando ? 'edicion' : 'seleccion';

  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        setEnvoltorio(ref);
        // `connect` solo cuando ya tiene posición: sin ella el click tiene que
        // seguir cayendo en la sección, como antes de que esto existiera.
        if (ref && posicionada) connect(ref);
      }}
      // `display: contents` no genera caja, pero `pointer-events` se hereda a
      // través suyo: es lo que le devuelve el mouse a la pieza cuando el área
      // que la contiene lo tiene apagado.
      style={{ display: 'contents', pointerEvents: 'auto' }}
      // El doble clic entra a editar. Va acá y no en cada campo porque toda la
      // pieza es la zona sensible: el evento burbujea desde donde se haya hecho.
      onDoubleClick={posicionada ? () => setEditando(true) : undefined}
      {...{ [ATRIBUTO_PIEZA]: id, [ATRIBUTO_TIPO_PIEZA]: datos?.tipo ?? 'bloque' }}
    >
      <ContextoModo.Provider value={modo}>{datos?.contenido ?? null}</ContextoModo.Provider>
    </div>
  );
};

(PiezaLibre as unknown as { craft: unknown }).craft = {
  displayName: 'Elemento',
  // Una pieza no se arrastra con el drag&drop de Craft ni se reordena entre
  // hermanos: pertenece a su sección. Se mueve con los cuadritos, como el resto
  // del canvas libre.
  rules: {
    canDrag: () => false,
    canDrop: () => false,
    canMoveIn: () => false,
  },
};

/**
 * Declara que un pedazo del JSX de la sección es una pieza manipulable.
 *
 * Es todo lo que hay que escribir para sumar una pieza:
 *
 * ```tsx
 * <Pieza clave="titulo" etiqueta="Título">
 *   <h1 className="text-5xl font-bold">…</h1>
 * </Pieza>
 * ```
 *
 * `clave` es la identidad estable de la pieza: entra en el JSON guardado, así
 * que se elige una vez y no se cambia. `etiqueta` es lo que el editor muestra
 * arriba de la caja de selección y sí se puede cambiar cuando se quiera.
 */
export const Pieza: React.FC<{
  clave: string;
  etiqueta: string;
  /** Qué es la pieza. De acá salen las manijas y la barra (ver `CAPACIDADES`). */
  tipo?: TipoPieza;
  /** Escape para un contenido con una regla propia; lo normal es no pasarlo. */
  politica?: PoliticaResize;
  children: React.ReactNode;
}> = ({ clave, etiqueta, tipo = 'bloque', politica, children }) => {
  const datos = useMemo(
    () => ({
      etiqueta,
      tipo,
      politica: politica ?? CAPACIDADES[tipo].politica,
      barra: CAPACIDADES[tipo].barra,
      contenido: children,
    }),
    [etiqueta, tipo, politica, children],
  );
  return (
    <ContextoPieza.Provider value={datos}>
      <Element id={`${PREFIJO_PIEZA}${clave}`} is={PiezaLibre} custom={{ pieza: clave }} />
    </ContextoPieza.Provider>
  );
};

/** Los nodos de pieza de una sección, en el orden en que están enlazados. */
export const idsDePiezas = (linkedNodes: Record<string, string> | undefined): string[] =>
  Object.entries(linkedNodes ?? {})
    .filter(([clave]) => clave.startsWith(PREFIJO_PIEZA))
    .map(([, id]) => id);

/**
 * Alto que hay que reservar en el flujo cuando la sección está congelada.
 *
 * Es el que tenía el contenedor de las piezas en el momento de congelar. Con
 * todas las piezas en `absolute` no queda nada que empuje: sin esto la sección
 * se desploma. Cero mientras ninguna pieza esté posicionada — y cero es lo que
 * hace que el área no toque nada.
 *
 * El respaldo por `y + h` es defensivo: cubre un árbol al que le falte el
 * `_altoPiezas` pero tenga piezas ya posicionadas.
 */
export const useAltoDePiezas = (idSeccion: string): number => {
  const { altoCongelado, fondos } = useEditor((state) => {
    const seccion = state.nodes[idSeccion]?.data;
    return {
      altoCongelado:
        Number((seccion?.props as Record<string, unknown> | undefined)?.[PROP_ALTO_PIEZAS]) || 0,
      fondos: idsDePiezas(seccion?.linkedNodes as Record<string, string> | undefined).map((id) => {
        const pos = normalizarPosicion(
          (state.nodes[id]?.data?.props as Record<string, unknown>)?.[PROP_POSICION],
        );
        return pos ? pos.y + (pos.h ?? 0) : 0;
      }),
    };
  });

  if (altoCongelado) return altoCongelado;
  const fondo = fondos.length ? Math.max(0, ...fondos) : 0;
  return fondo;
};

/**
 * Alto que hay que reservar ABAJO de `xl` por las piezas con posición móvil.
 *
 * `useAltoDePiezas` reserva el alto de ARRIBA de `xl`, donde el área flota y no
 * hay nada en el flujo. Abajo del corte el espaciador se apaga a propósito
 * (`CLASES_ESPACIADOR`) porque ahí las piezas vuelven al flujo y empujan solas
 * — pero eso vale sólo para las que NO tienen `_layout.m`. Una pieza CON
 * coordenadas móviles sigue siendo `absolute` también abajo del corte, así que
 * no empuja nada y la sección se mide como si no existiera.
 *
 * Medido en la home de Concecars a 390px: la foto del auto terminaba en y=519
 * y su sección en y=468, o sea 51px de contenido por fuera, tapados por la
 * sección siguiente.
 *
 * Se miran las piezas de la sección Y los hijos de su capa libre: una y otra
 * resuelven sus coordenadas móviles contra el mismo ancestro posicionado, y la
 * que se sale es indistintamente cualquiera de las dos.
 *
 * Cero cuando ninguna pieza tiene `m`, que es el caso de todo lo guardado antes
 * de que existiera la edición en móvil: ahí no se emite ni una declaración y el
 * HTML es el de siempre, carácter por carácter.
 */
export const useAltoMovilDePiezas = (idSeccion: string): ReservaMovil => {
  // El colector devuelve un OBJETO y no el array a propósito: `useEditor` le
  // agrega lo suyo (`actions`, `query`…) a lo que uno devuelva, así que un array
  // suelto vuelve convertido en objeto y `.length` queda en `undefined`.
  const { fondos } = useEditor((state) => {
    const seccion = state.nodes[idSeccion]?.data;
    const idCapa = (seccion?.linkedNodes as Record<string, string> | undefined)?.[ID_CAPA_LIBRE];
    const ids = [
      ...idsDePiezas(seccion?.linkedNodes as Record<string, string> | undefined),
      ...(idCapa ? state.nodes[idCapa]?.data?.nodes ?? [] : []),
    ];
    return {
      fondos: ids.map((id) => {
        const nodo = state.nodes[id]?.data;
        const pos = normalizarPosicion((nodo?.props as Record<string, unknown>)?.[PROP_POSICION]);
        if (!pos?.m) return null;
        // Un marco que se mide por proporción no tiene un alto en píxeles que
        // reservar: su alto es una fracción del ancho, y eso viaja aparte.
        const pct = esMarco(nodo?.name) ? altoRelativoMovil(pos) : 0;
        return pct
          ? { y: pos.m.y, pct }
          : { px: pos.m.y + (pos.m.h ?? 0) };
      }).filter(Boolean) as Array<{ px: number } | { y: number; pct: number }>,
    };
  });

  const pisos = fondos.filter((f): f is { px: number } => 'px' in f).map((f) => f.px);
  return {
    piso: pisos.length ? Math.max(0, ...pisos) : 0,
    relativas: fondos.filter((f): f is { y: number; pct: number } => 'pct' in f),
  };
};

/**
 * La reserva de alto de una sección abajo de `lg`, en sus dos formas.
 *
 * `piso` son píxeles y sale de las piezas cuyo alto móvil ES un número de
 * píxeles. `relativas` son los marcos que se miden por proporción: su alto es
 * un porcentaje del ANCHO, así que no se puede sumar con lo otro ni comparar
 * acá — la comparación la hace el CSS con `max()`, que es el único que conoce
 * el ancho real.
 */
export interface ReservaMovil {
  piso: number;
  relativas: Array<{ y: number; pct: number }>;
}

/**
 * Estilo del área una vez congelada.
 *
 * ## Por qué se despega
 *
 * En el diseño original este `<div>` es el contenedor central del contenido, con
 * su `max-width`, su padding y su `text-align`. Mientras las piezas estaban
 * atadas a él, el canvas terminaba donde terminaba ESA caja: un título no podía
 * llegar al borde de la sección porque el tope horizontal salía de un contenedor
 * de 1280px centrado. Era el problema de fondo — el área hacía de límite del
 * diseño viejo en vez de límite de la sección.
 *
 * Congelada, el área pasa a `absolute; inset: 0`, así que se estira sobre TODA
 * la superficie de su marco (la sección) y pasa a ser el bloque contenedor de
 * las piezas: el mismo elemento hace de sistema de coordenadas, de tope del
 * arrastre y de superficie útil. La caja que había en el flujo la reemplaza un
 * espaciador del alto que tenía, para que la sección no cambie de tamaño.
 *
 * El padding y el `display` se anulan porque acá ya no hay nada en el flujo que
 * acomodar, y un padding correría el origen de las coordenadas. El resto de las
 * clases (`text-center`, tipografías heredadas) se conservan: son parte del
 * diseño que las piezas siguen heredando.
 */
const ESTILO_AREA_CONGELADA: React.CSSProperties = {
  // `position` e `inset` NO van acá: viajan en `CLASES_AREA_CONGELADA` para que
  // puedan depender del breakpoint. Un estilo en línea no entiende de `xl:`, y
  // abajo de `lg` esta área NO puede seguir flotando (ver esas clases).
  // `inset: 0` por sí solo NO estira: un `max-width` del diseño original le gana
  // —una caja de 1024px con `mx-auto` se queda en 1024 y se centra— y el canvas
  // volvía a terminar donde terminaba el contenedor viejo. Hay que apagar
  // explícitamente todo lo que fije o limite la medida.
  width: 'auto',
  height: 'auto',
  maxWidth: 'none',
  maxHeight: 'none',
  minWidth: 0,
  margin: 0,
  // Un padding correría el origen de las coordenadas, y acá ya no hay nada en el
  // flujo que acomodar.
  padding: 0,
  display: 'block',
  // Abajo de `xl` el área está EN FLUJO, y si el contenedor al que reemplaza es
  // un flex —`flex items-center`, como la cabecera con video— entonces el área
  // es un flex item: con `width:auto` se mide por su contenido, y su contenido
  // son puras piezas `absolute`, o sea nada. Colapsaba a 0x0 y encima quedaba
  // centrada por el `items-center` del padre. Medido en la home de Ansar a
  // 390px: área 0x0, el `<h1>` heredaba ancho 0 y aterrizaba en y=1098 cuando la
  // sección termina en 912 — el texto del hero no se veía.
  //
  // `flex-grow` y `align-self` no existen para un elemento `absolute`, así que
  // desde `lg` —donde el área es `absolute; inset:0`— estas dos líneas no hacen
  // nada. Y si el contenedor no es flex tampoco: un bloque con `width:auto` ya
  // ocupaba todo el ancho.
  flexGrow: 1,
  alignSelf: 'stretch',
  // Misma regla que la capa de elementos nuevos (`ESTILO_CAPA`): el área tapa la
  // sección entera, así que si recibiera el mouse se comería todo clic que no
  // caiga sobre una pieza —incluido el del fondo de la sección— y le disputaría
  // el puntero a la otra capa. Las piezas lo vuelven a habilitar.
  pointerEvents: 'none',
};

/**
 * Dónde se posiciona el área congelada, por breakpoint.
 *
 * Desde `xl` es lo de siempre: `absolute; inset:0`, estirada sobre la sección y
 * haciendo de sistema de coordenadas de las piezas.
 *
 * Abajo de `xl` vuelve al FLUJO. Una pieza sin `_layout.m` se dibuja `relative`
 * —así lo decide `clasesDePosicion`— y dentro de un área flotante eso la mandaba
 * al borde superior de la sección, encima del contenido y desbordando hacia la
 * de abajo. Medido en /taller de Ansar a 375px: hasta 148px de contenido fuera
 * de su sección. En flujo las piezas se apilan una detrás de otra, que es lo que
 * el diseño original hacía antes de congelar.
 *
 * Una pieza CON `_layout.m` no cambia: sigue `absolute` y, con el área en
 * estático, resuelve contra el mismo ancestro posicionado de antes.
 *
 * MANTENER EN SYNC entre goautos-admin y website-gocar, y con la regla hermana
 * de la capa libre (`globals.css` / `SITE_CANVAS_CSS`).
 */
const CLASES_AREA_CONGELADA = 'static lg:absolute lg:inset-0';

/**
 * El espaciador repone en el flujo lo que las piezas dejaron de empujar al
 * volverse `absolute`. Abajo de `xl` ya no hace falta —las piezas vuelven al
 * flujo y empujan solas— y dejarlo duplicaría el alto de la sección.
 */
const CLASES_ESPACIADOR = 'hidden lg:block';

/**
 * El piso de alto que las piezas con posición móvil le ponen al área.
 *
 * Va en el ÁREA y no en el envoltorio de la sección a propósito: el área vive
 * adentro del `<div>` que pinta el fondo, así que estirarla estira el fondo con
 * ella. Puesto más arriba, la sección crecía pero su color terminaba donde
 * terminaba antes y la pieza quedaba sobre el fondo de la página.
 *
 * `min-height` y no `height`: abajo del corte el área también tiene piezas en
 * el flujo, y si piden más alto que las móviles mandan ellas.
 *
 * `lg:min-h-0` porque desde `lg` el área es `absolute; inset:0` y su alto sale
 * de ahí: un piso propio podría estirarla más allá de su sección.
 */
const CLASES_ALTO_MOVIL = 'min-h-[var(--piezas-mh)] lg:min-h-0';

/**
 * La reserva de un marco que se mide por proporción.
 *
 * Su alto es un PORCENTAJE DEL ANCHO, y un porcentaje en `min-height` se
 * resuelve contra el alto —o sea, contra nada— así que ahí no sirve. El único
 * largo que en CSS se resuelve contra el ancho es un padding en porcentaje: por
 * eso la reserva viaja como `padding-bottom`, que agrega alto al final sin
 * correr el origen de las coordenadas.
 *
 * El valor va en el estilo EN LÍNEA porque `ESTILO_AREA_CONGELADA` ya declara
 * `padding: 0` ahí mismo y una clase no le ganaría. Y como en línea no hay
 * breakpoints, arriba de `lg` se apaga con una SEGUNDA variable:
 *
 *     en línea:  --piezas-mpb: calc(…)
 *                padding-bottom: var(--piezas-mpb-off, var(--piezas-mpb))
 *     por clase: xl:[--piezas-mpb-off:0px]
 *
 * Son dos y no una a propósito: una declaración en línea le gana a cualquier
 * regla de hoja de estilos, media query incluida, así que redefinir desde `lg`
 * la MISMA variable que se escribió en línea no apaga nada. El apagador tiene
 * que ser una variable que en línea no exista, para que abajo del corte el
 * `var()` caiga en su respaldo.
 */
const CLASES_RESERVA_MOVIL = 'lg:[--piezas-mpb-off:0px]';

/** Redondea a 4 decimales: más precisión no cambia un píxel y ensucia el DOM. */
const pct = (n: number) => Math.round(n * 10000) / 10000;

/**
 * La expresión CSS de la reserva relativa. Con más de un marco gana el más
 * alto, y quien sabe cuál es a ese ancho es el navegador, no nosotros.
 */
const reservaRelativa = (relativas: Array<{ y: number; pct: number }>): string => {
  if (!relativas.length) return '';
  // El porcentaje se mide contra el ancho EFECTIVO de la composición, el mismo
  // que usa el marco para dibujarse (`ANCHO_COMPOSICION_MOVIL`). Con un `%` a
  // secas la reserva seguiría calculándose sobre el contenedor entero mientras
  // el marco ya está topeado, y la sección se quedaría con un hueco al pie de
  // hasta el alto del propio marco. Abajo del tope las dos expresiones valen lo
  // mismo, así que nada de lo que hoy se ve en un teléfono cambia.
  const partes = relativas.map(
    (r) => `calc(${r.y}px + ${pct(r.pct)} * ${ANCHO_COMPOSICION_MOVIL} / 100)`,
  );
  return partes.length === 1 ? partes[0] : `max(${partes.join(', ')})`;
};


/**
 * El contenedor donde viven las piezas y contra el que se miden sus coordenadas.
 *
 * Reemplaza a un `<div>` que la sección ya tenía, con sus mismas clases: mientras
 * ninguna pieza esté posicionada no agrega ni un estilo, así que el DOM sigue
 * siendo el de siempre.
 *
 * **Al convertir una sección**: el ancestro posicionado más cercano de este
 * elemento tiene que ser la raíz de la sección, o un `<div>` que la cubra
 * entera. Es el marco sobre el que se estira el área congelada, y de él salen
 * los límites del arrastre.
 */
export const AreaDePiezas: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ className, style, children }) => {
  const { id } = useNode();
  const alto = useAltoDePiezas(id);
  const { piso, relativas } = useAltoMovilDePiezas(id);
  // Sólo con el diseño congelado: sin eso el área sigue siendo el contenedor
  // original de la sección, con su padding y su `max-width`, y ponerle un piso
  // de alto le cambiaría el tamaño a una sección que nadie tocó.
  const pisoMovil = alto ? piso : 0;
  const relativa = alto ? reservaRelativa(relativas) : '';

  const area = (
    <div
      className={[
        className,
        alto ? CLASES_AREA_CONGELADA : '',
        pisoMovil ? CLASES_ALTO_MOVIL : '',
        relativa ? CLASES_RESERVA_MOVIL : '',
      ].filter(Boolean).join(' ')}
      style={
        alto
          ? {
              ...style,
              ...ESTILO_AREA_CONGELADA,
              ...(pisoMovil ? ({ ['--piezas-mh']: `${pisoMovil}px` } as React.CSSProperties) : {}),
              // Después de `ESTILO_AREA_CONGELADA` a propósito: le pisa el
              // `padding: 0` sólo en el borde de abajo y sólo si hay reserva.
              ...(relativa
                ? ({
                    ['--piezas-mpb']: relativa,
                    paddingBottom: 'var(--piezas-mpb-off, var(--piezas-mpb))',
                  } as React.CSSProperties)
                : {}),
            }
          : style
      }
      {...{ [ATRIBUTO_AREA_PIEZAS]: id }}
      // El área solo se ofrece como marco de referencia cuando el diseño ya está
      // congelado: antes de eso no hay nada absoluto que referenciar.
      {...(alto ? { [ATRIBUTO_AREA_LIBRE]: '' } : {})}
    >
      {children}
    </div>
  );

  if (!alto) return area;

  return (
    <>
      {/* Ocupa en el flujo lo que ocupaba el contenido, para que la sección
          conserve su alto ahora que las piezas no empujan nada. */}
      <div aria-hidden className={CLASES_ESPACIADOR} style={{ minHeight: `${alto}px` }} />
      {area}
    </>
  );
};
